import { useState, useRef, useEffect, useMemo } from "react";
import { cn } from "../../lib/utils";
import { searchAdresse } from "../../api/geo";

function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

export default function AddressAutocomplete({
  label,
  name,
  value,
  onChange,
  onSelect,
  required,
  className,
  error,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const fetchSuggestions = useMemo(
    () =>
      debounce(async (query) => {
        if (!query || query.length < 3) {
          setSuggestions([]);
          setOpen(false);
          return;
        }
        try {
          const results = await searchAdresse(query);
          const items = Array.isArray(results) ? results : [];
          setSuggestions(items);
          setOpen(items.length > 0);
          setActiveIndex(-1);
        } catch {
          // Silently fail — user can still type manually
        }
      }, 300),
    []
  );

  const handleChange = (e) => {
    onChange(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const handleSelect = (item) => {
    // Extract département number from context (format: "75, Paris, Île-de-France")
    const departement = item.departement
      ? item.departement.split(",")[0].trim()
      : "";

    onSelect({
      adresse: item.label,
      code_postal: item.code_postal || "",
      commune: item.commune || "",
      departement,
      latitude: item.latitude,
      longitude: item.longitude,
      cle_interop: item.cle_interop || "",
      code_insee: item.code_insee || "",
    });

    setSuggestions([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative space-y-1">
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-slate-700"
        >
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        id={name}
        name={name}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        required={required}
        autoComplete="off"
        className={cn(
          "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm",
          "focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500",
          "transition-colors duration-200",
          error && "border-red-500 focus:ring-red-500 focus:border-red-500",
          className
        )}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}

      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((item, index) => (
            <li
              key={`${item.label}-${index}`}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer",
                index === activeIndex
                  ? "bg-carex-50 text-carex-700"
                  : "hover:bg-slate-50"
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur before click
                handleSelect(item);
              }}
            >
              <span className="font-medium">
                {item.numero ? `${item.numero} ${item.rue}` : item.rue || item.label}
              </span>
              {item.code_postal && (
                <span className="text-slate-500 ml-1">
                  — {item.code_postal} {item.commune}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
